import React, { useCallback } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import type { PackageOptionViewByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getActivePackagesByRoom, getAvailablePackagesByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { arePricesLoading } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextLink from 'LuxKit/TextLink'
import { useIntl } from 'react-intl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import MultipleRoomsIndividualRoomsFooter from '../../MultipleRoomsBooking/MultipleRoomsIndividualRoomsFooter'
import Sheet from 'components/utils/Sheet'
import MultiIndividualRoomRoom from '../../MultipleRoomsBooking/MultiIndividualRoomRoom'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

const NoPackagesContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  rooms: Array<OfferPageRoom>
  multipleRoomPackages: PackageOptionViewByRoom
  availablePackagesByRoom: PackagesOptionsViewsByRoom
  pricesLoading: boolean
}

function FlashHotelMobileIndividualRooms({ rooms, multipleRoomPackages, availablePackagesByRoom, pricesLoading }: Props) {
  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const onOpenCapacity = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  return (
    <Sheet background="neutral-six">
      <Group direction="vertical" gap={8}>
        {rooms.map((room, index) => {
          if (pricesLoading) {
            return <NoPackagesContainer key={room.id}>
              <LoadingIndicator inline />
            </NoPackagesContainer>
          }

          const packageView = multipleRoomPackages[room.id]
          if (!packageView) {
            const roomNumberText = intl.formatMessage(
              {
                id: 'foZ861S4y-qvE2WdJk_v',
                defaultMessage: 'Room {roomNumber}',
                description: 'Label displaying the room number for a specific room in the hotel booking form',
              },
              {
                roomNumber: index + 1,
              },
            )

            const noRoomAvailableText = intl.formatMessage({
              id: 't1xHlY-muddj9_53eKdd',
              defaultMessage: 'No room available',
              description: 'Message when no room is available on the hotel or room booking page',
            })

            const reviseGuestsMessage = intl.formatMessage(
              {
                id: 'VC7jsJl_nhiB7tbr71nS',
                defaultMessage: 'No room available. Please <link>revise number of guests</link>.',
                description: 'Message asking user to revise number of guests on the offer detail page',
              },
              {
                link: (chunks: React.ReactNode) => (
                  <TextLink key="revise-link" weight="regular" onClick={onOpenCapacity}>
                    {chunks}
                  </TextLink>
                ),
              },
            )

            return <NoPackagesContainer key={room.id}>
              <Group direction="vertical" gap={16}>
                <Group direction="vertical" gap={12}>
                  <StructureLabel variant="dark">{roomNumberText}</StructureLabel>
                  <div>
                    <Heading variant="heading4">{noRoomAvailableText}</Heading>
                    <BodyText variant="medium" colour="neutral-three">{formatOccupantsShort(room)}</BodyText>
                  </div>
                </Group>
                <MessageBanner kind="warning" density="compact">
                  {reviseGuestsMessage}
                </MessageBanner>
              </Group>
            </NoPackagesContainer>
          }

          return <MultiIndividualRoomRoom
            key={room.id}
            room={room}
            packageNumber={index + 1}
            packageView={packageView}
            isFetching={pricesLoading}
            availablePackages={availablePackagesByRoom[room.id]}
          />
        })}

        <MultipleRoomsIndividualRoomsFooter />
      </Group>
    </Sheet>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  pricesLoading: arePricesLoading(appState, state),
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
}), FlashHotelMobileIndividualRooms)
